import useCourseBuilderContentPermissions from "@src/features/libraryItem/course/hooks/useCourseBuilderContentPermissions";
import useModuleBuilderContentPermission from "@src/features/libraryItem/module/hooks/useModuleBuilderContentPermission";
import useSkillBuilderContentPermissions from "@src/features/libraryItem/skill/hooks/useSkillBuilderContentPermissions";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useUser from "@src/features/app/hooks/useUser";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useActiveLibraryItemMainTab } from "@src/features/libraryItem/contexts/libraryItemMainTabStore";
import { selectedTrainingResourceIdAtom } from "@src/features/libraryItem/resource/atoms";
import useContentPermissions from "@src/features/libraryItem/hooks/useContentPermissions";

type BuilderContentPermissions = {
  hasEditPermission: boolean;
};

export const useBuilderContentPermissions = (): BuilderContentPermissions => {
  const user = useUser();
  const { userActionPermissions } = useActionPermissions();
  const skillBuilderPermissions = useSkillBuilderContentPermissions();
  const moduleBuilderPermissions = useModuleBuilderContentPermission();
  const courseBuilderPermissions = useCourseBuilderContentPermissions();
  const trainingResourceId = useAtomValue(selectedTrainingResourceIdAtom);
  const trainingResourcePermissions = useContentPermissions({
    trainingResourceId,
  });
  const trainingResourceBuilderPermissions =
    user.isStaff ||
    (!skillBuilderPermissions &&
      !moduleBuilderPermissions &&
      !courseBuilderPermissions &&
      (!!trainingResourcePermissions?.hasEditContentPermission ||
        !!userActionPermissions?.manageLibrary));
  // The redesigned page's "preview" tab renders the draft read-only.
  // Everything on the canvas inherits this — including the check-ins,
  // resources, and courses embedded in it, whose own content permissions
  // know nothing about the mode. Null on the previous page, so nothing
  // changes there.
  const viewingTab = useActiveLibraryItemMainTab() === "preview";
  const hasEditPermission = Boolean(
    !viewingTab &&
      (moduleBuilderPermissions?.hasEditContentPermission ||
        courseBuilderPermissions?.hasEditContentPermission ||
        skillBuilderPermissions?.hasEditContentPermission ||
        trainingResourceBuilderPermissions),
  );
  return useMemo(() => {
    return {
      hasEditPermission,
    };
  }, [hasEditPermission]);
};
